import Vue from "vue";
import Vuex from "vuex";
import App from "./App.vue";
import { db } from "./firebaseConfig.js";
import { shuffle } from "./utils.js";
import firebase from "firebase/app";
import "firebase/firestore";

const NUMBER_OF_CARDS = {
  'dixit': 106,
  'fbanna': 69,
}

Vue.use(Vuex);
Vue.config.productionTip = false;

class GameState {
  constructor() {
    this.actions = []
    this.reset()
  }

  // We need to always return a new object because Vue's reactive watcher is not very smart
  getState() {
    return {
      players: {...this.players},
      playersOrder: [...this.playersOrder],
      cards: [...this.cards],
      round: this.round,
      roundPhase: this.roundPhase,
      variant: this.variant,
    }
  }

  reset() {
    this.players = {}
    this.playersOrder = []
    this.cards = []
    this.round = 0
    this.roundPhase = 'SELECTING'
    this.variant = null
  }

  handleAction(action, payload, seq) {
    console.log('Handling action:', seq, action, payload)

    // If the action already exists, we can ignore it
    const existingAction = this.actions.find(a => a.seq === seq)
    if (existingAction) {
      return
    }

    console.log('Action is new, recalculating state')

    this.actions.push({ action, payload, seq })
    this.actions.sort((a, b) => a.seq - b.seq)

    this.reset()

    for (let a of this.actions) {
      this.processAction(a.action, a.payload)
    }
  }

  processAction(action, payload) {
    const handlers = {
      'create_game': ({ cards, variant }) => {
        this.cards = cards
        this.variant = variant
      },
      'player_joined': ({ playerName }) => {
        if (this.players[playerName]) {
          return
        }
        this.players[playerName] = {
          name: playerName,
          score: 0,
          round: 0,
          cards: [],
          selectedCard: null,
          votedCard: null,
          readyToMoveToNextRound: false
        }
        this.playersOrder.push(playerName)

        // Draw first 5 cards
        if (this.cards.length < 5) {
          alert('The deck is empty')
          throw new Error('The deck is empty')
        }
        const cards = this.cards.slice(0, 5)
        this.players[playerName].cards = cards
        this.cards = this.cards.slice(5)
      },
      'player_ready_to_move_to_next_round': ({ playerName }) => {
        this.players[playerName].readyToMoveToNextRound = true

        // If everyone is ready, then we update scores and move to the next round
        if (Object.values(this.players).every(p => p.readyToMoveToNextRound)) {
          const currentPlayer = this.playersOrder[this.round % this.playersOrder.length]
          const numberOfPeopleWhoGuessedCorrectly = Object.values(this.players)
            .filter(player => player.name !== currentPlayer)
            .filter(player => player.votedCard === this.players[currentPlayer].selectedCard)
            .length;

          // Add score to the player whose turn it was
          if (
            numberOfPeopleWhoGuessedCorrectly > 0 &&
            numberOfPeopleWhoGuessedCorrectly < this.playersOrder.length - 1 /* self */
          ) {
            this.players[currentPlayer].score += 3;
          }

          // Add score to the other players if someone chose their card
          this.playersOrder
            .filter(p => p !== currentPlayer)
            .forEach(p => {
              this.players[p].score += Object.values(this.players).filter(otherPlayer => otherPlayer.votedCard === this.players[p].selectedCard).length
            })

          // Add score to those who guessed correctly
          this.playersOrder
            .filter(p => p !== currentPlayer)
            .forEach(p => {
              if (this.players[p].votedCard === this.players[currentPlayer].selectedCard) {
                this.players[p].score += 3
              }
            })

          this.round += 1
          this.roundPhase = 'SELECTING'
          Object.values(this.players).forEach(p => {
            p.cards = p.cards.filter(c => c !== p.selectedCard)
            p.selectedCard = null
            p.votedCard = null
            p.readyToMoveToNextRound = false
          })

          // Everyone draws a card
          this.playersOrder.forEach(p => {
            if (this.cards.length === 0) {
              alert('The deck is empty')
              throw new Error('The deck is empty')
            }
            const card = this.cards.pop()
            this.players[p].cards.push(card)
          })
        }
      },
      'player_drew_card': ({ playerName }) => {
        if (this.cards.length === 0) {
          alert('The deck is empty')
          throw new Error('The deck is empty')
        }
        const card = this.cards.pop()
        this.players[playerName].cards.push(card)
      },
      'player_selected_card': ({ playerName, card }) => {
        this.players[playerName].selectedCard = card

        if (Object.values(this.players).every(p => p.selectedCard)) {
          this.roundPhase = 'VOTING'

          // The current player automatically votes for their own card
          const currentPlayer = this.playersOrder[this.round % this.playersOrder.length]
          this.players[currentPlayer].votedCard = this.players[currentPlayer].selectedCard
        }
      },
      'player_voted_card': ({ playerName, card }) => {
        this.players[playerName].votedCard = card

        if (Object.values(this.players).every(p => p.votedCard)) {
          this.roundPhase = 'REVIEWING'
        }
      },
    }

    if (handlers[action]) {
      handlers[action](payload)
    } else {
      throw new Error(`Unrecognized action ${action}`)
    }
  }
}
const gameState = new GameState()
window.gameState = gameState

async function triggerAction(room, action, payload) {
  await db.runTransaction(async transaction => {
    const roomRef = db.collection('rooms').doc(room);
    const roomData = ( await transaction.get( roomRef ) ).data();
    const seq = roomData.actionsCount + 1;
    transaction.update(roomRef, { actionsCount: seq });

    await db
      .collection("rooms")
      .doc(room)
      .collection("actions")
      .add({ seq, action, payload, timestamp: firebase.firestore.FieldValue.serverTimestamp() })
  });
}

const store = new Vuex.Store({
  state: {
    roomId: window.location.hash,
    localPlayerId: null,
    gameState: null,

    playerNameInput: undefined
  },
  getters: {
    screen: (state) => {
      if (!state.roomId) {
        return "intro";
      }
      if (!state.localPlayerId) {
        return "join-game";
      }
      if (state.gameState && state.gameState.roundPhase === "SELECTING") {
        return "game";
      }
      if (state.gameState && state.gameState.roundPhase === "VOTING") {
        return "game";
      }
      if (state.gameState && state.gameState.roundPhase === "REVIEWING") {
        return "game-reviewing";
      }
    },
    players: state => {
      return Object.values(state.gameState.players)
    },
    playersNotReadyToMoveToNextRound: state => {
      return Object.values(state.gameState.players).length - Object.values(state.gameState.players).filter(p => p.readyToMoveToNextRound).length
    },
    turn: state => {
      return state.gameState.playersOrder[state.gameState.round % state.gameState.playersOrder.length]
    },
    localPlayer: (state) => {
      return Object.values(state.gameState.players).find(p => p.name === state.localPlayerId);
    },
    isMyTurn: (state, getters) => {
      return getters.turn === state.localPlayerId;
    },
    votingOptionCards: (state, getters) => {
      return Object.values(state.gameState.players)
        .filter(p => p.name !== getters.localPlayer.name)
        .map(p => p.selectedCard);
    },
    isReadyToMoveToNextRound: (state, getters) => {
      return getters.localPlayer.readyToMoveToNextRound
    },
  },
  mutations: {
    setPlayerNameInput(state, value) {
      state.playerNameInput = value;
    },
    setPlayerName(state, value) {
      state.localPlayerId = value;
    },
    setGameState(state, value) {
      state.gameState = value;
    },
  },
  actions: {
    async createGame(context, variant) {
      const id = Math.random().toString(24).slice(9);
      await db.collection("rooms").doc(`#${id}`).set({
        variant,
        actionsCount: 0
      })
      await triggerAction(`#${id}`, 'create_game', {
        variant,
        cards: shuffle([...Array(NUMBER_OF_CARDS[variant]).keys()].map(i => i + 1))
      })
      window.location.hash = id
      window.location.reload()
    },
    async joinGame({ commit, state }) {
      if (!state.playerNameInput) {
        return;
      }
      if (state.playerNameInput === "") {
        return;
      }
      commit("setPlayerName", state.playerNameInput);

      // Make sure the game with this id exists
      const existingRoomRef = await db.collection("rooms").doc(state.roomId).get();
      if (!existingRoomRef.exists) {
        alert('A room with this ID doesn\'t exist')
        return
      }

      // Populate state based on previous actions
      const previousActionsRef = await db.collection("rooms").doc(state.roomId).collection('actions').orderBy('timestamp').get()
      console.log('Replaying existing actions')
      previousActionsRef.forEach(doc => {
        const data = doc.data()
        gameState.handleAction(data.action, data.payload, data.seq)
      })
      commit("setGameState", gameState.getState())

      // Register event handlers
      console.log('Listening to new actions')
      await db
        .collection("rooms")
        .doc(state.roomId)
        .collection("actions")
        .orderBy('seq')
        .onSnapshot(function(snapshot) {
          snapshot.docChanges().forEach(function(change) {
            if (change.type === "removed") {
              throw new Error('Removing actions is not possible')
            }

            if (change.type === "modified") {
              // Firebase modifies entries, presumably due to timestamp, which
              // causes duplicate entries, so we are ignoring this
              return
            }

            const data = change.doc.data();
            gameState.handleAction(data.action, data.payload, data.seq)
            commit("setGameState", gameState.getState())
          });
        });

      await triggerAction(state.roomId, 'player_joined', {
        playerName: state.localPlayerId
      })
    },
    async drawCardFromDeck({ state }) {
      await triggerAction(state.roomId, 'player_drew_card', {
        playerName: state.localPlayerId
      })
    },
    async selectCard({ state }, card) {
      await triggerAction(state.roomId, 'player_selected_card', {
        playerName: state.localPlayerId,
        card
      })
    },
    async voteCard({ state }, card) {
      await triggerAction(state.roomId, 'player_voted_card', {
        playerName: state.localPlayerId,
        card
      })
    },
    async markAsReadyToMoveToNextRound({ state }) {
      await triggerAction(state.roomId, 'player_ready_to_move_to_next_round', {
        playerName: state.localPlayerId,
      })
    }
  }
});

new Vue({
  render: h => h(App),
  store: store
}).$mount("#app");

// Setting height as 100% or 100vh is not sufficient, because it doesn't take into account the addressbar on mobile devices
// First we get the viewport height and we multiple it by 1% to get a value for a vh unit
// Then we set the value in the --vh custom property to the root of the document
let vh = window.innerHeight * 0.01;
document.documentElement.style.setProperty('--vh', `${vh}px`);
window.addEventListener('resize', () => {
  let vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
});